<template>
  <v-container>
    <v-card flat>
      <v-card-title>
        Task Settings
        <v-spacer/>
        <v-btn :disabled="readOnly || saving || !hasUnsavedChanges" :loading="saving" color="primary" @click="save">
          Save
        </v-btn>
      </v-card-title>
      <v-card-subtitle>
        Deadline times for the P1/P2/P3 late warnings shown on the task pages.
        Every change is recorded with the date so past task performance can be analysed
        against the deadlines that were active at the time.
      </v-card-subtitle>
      <v-alert v-if="readOnly" class="mx-4" dense type="info">
        You have read-only access to this page.
      </v-alert>
      <v-alert v-if="hasUnsavedChanges" class="mx-4" dense type="warning">
        You have unsaved changes. Click Save to apply them.
      </v-alert>
      <v-card-text v-if="loading">Loading settings...</v-card-text>
      <v-card-text v-if="!loading">
        <v-row>
          <v-col v-for="(deadlines, team) of teamDeadlines" v-bind:key="team" cols="12" md="6">
            <v-card outlined>
              <v-card-title class="text-capitalize">{{ team }}</v-card-title>
              <v-card-text>
                <v-text-field
                  v-for="priority of priorities"
                  v-bind:key="priority"
                  v-model="deadlines[priority]"
                  :disabled="readOnly"
                  :label="`P${priority} deadline`"
                  type="time"
                />
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card class="mt-4" flat>
      <v-card-title>Change History</v-card-title>
      <v-card-text v-if="history.length === 0">No changes recorded yet (defaults in use).</v-card-text>
      <v-simple-table v-if="history.length > 0" dense>
        <thead>
        <tr>
          <th>Changed</th>
          <th>By</th>
          <th v-for="team of teams" v-bind:key="team" class="text-capitalize">{{ team }}</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="entry of history" v-bind:key="entry.id">
          <td>{{ formatDate(entry.created_at) }}</td>
          <td>{{ entry.created_by }}</td>
          <td v-for="team of teams" v-bind:key="team">{{ formatDeadlines(entry.value[team]) }}</td>
        </tr>
        </tbody>
      </v-simple-table>
    </v-card>

    <v-snackbar v-model="showSnackbar" :color="snackbarColor" timeout="3000" top>
      {{ snackbarMessage }}
    </v-snackbar>
  </v-container>
</template>

<script>
import moment from 'moment';
import api from '@/api';
import {
  DEFAULT_PRIORITY_DEADLINES,
  fetchPriorityDeadlines,
  PRIORITY_DEADLINES_KEY
} from '@/utils/tasks/priority-deadlines';

export default {
  name: "TaskSettings",
  props: {
    readOnly: {type: Boolean, default: false, required: false},
  },
  data() {
    return {
      loading: true,
      saving: false,
      teamDeadlines: {},
      savedSnapshot: null,
      history: [],
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success',
    }
  },
  computed: {
    teams() {
      return Object.keys(DEFAULT_PRIORITY_DEADLINES);
    },
    priorities() {
      return Object.keys(DEFAULT_PRIORITY_DEADLINES[this.teams[0]]);
    },
    hasUnsavedChanges() {
      return this.savedSnapshot !== null && JSON.stringify(this.teamDeadlines) !== this.savedSnapshot;
    },
  },
  async mounted() {
    this.teamDeadlines = await fetchPriorityDeadlines();
    this.savedSnapshot = JSON.stringify(this.teamDeadlines);
    this.loading = false;
    this.fetchHistory();
  },
  beforeRouteLeave(to, from, next) {
    if (this.hasUnsavedChanges && !confirm('You have unsaved changes. Leave without saving?')) {
      return next(false);
    }
    next();
  },
  methods: {
    async fetchHistory() {
      try {
        const {data} = await api.get(`v2/settings/${PRIORITY_DEADLINES_KEY}/history`);
        this.history = data;
      } catch (e) {
        console.error('failed to fetch settings history', e);
      }
    },
    async save() {
      this.saving = true;
      try {
        await api.put(`v2/settings/${PRIORITY_DEADLINES_KEY}`, {value: this.teamDeadlines});
        this.savedSnapshot = JSON.stringify(this.teamDeadlines);
        this.snackbarMessage = 'Settings saved';
        this.snackbarColor = 'success';
        this.fetchHistory();
      } catch (e) {
        console.error('failed to save settings', e);
        this.snackbarMessage = `Failed to save: ${e.message}`;
        this.snackbarColor = 'error';
      }
      this.showSnackbar = true;
      this.saving = false;
    },
    formatDate(date) {
      return moment(date).format('ddd MMM D YYYY, HH:mm');
    },
    formatDeadlines(deadlines) {
      if (!deadlines) {
        return '';
      }
      return Object.entries(deadlines).map(([p, time]) => `P${p} ${time}`).join(', ');
    },
  }
}
</script>

<style scoped>

</style>
